"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { H3 } from "@yardzen/ui/components/h3";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import {
  TypeStarRatingSkeleton,
  TypeReviewHighlight,
  TypeReviewSkeleton,
} from "@contentful/types";
import { StarRating } from "./StarRating";

export type ReviewHighlightProps = {
  starRating?: Entry<TypeStarRatingSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
  reviewHighlight?: TypeReviewHighlight<"WITHOUT_UNRESOLVABLE_LINKS">;
};

export function ReviewHighlight({
  starRating,
  className,
  reviewHighlight,
}: ReviewHighlightProps) {
  const linkedReview = reviewHighlight?.fields.review as
    | Entry<TypeReviewSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">
    | undefined;

  const mappedQuote = linkedReview?.fields.review || "";
  const mappedName = linkedReview?.fields.name || "";
  const mappedSubtitle =
    linkedReview?.fields.location || linkedReview?.fields.package || "";

  const mappedStarRating =
    starRating ||
    (linkedReview?.fields.starRating as
      | Entry<TypeStarRatingSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">
      | undefined);

  const Stars = mappedStarRating ? (
    <StarRating starRating={mappedStarRating} variant="center" />
  ) : (
    <div
      className="mb-6 flex items-center justify-center gap-1 fill-[#E19B34] text-[#E19B34]"
      aria-label="5 star rating"
    >
      {[...Array(5)].map((_, i) => (
        <FontAwesomeIcon key={i} icon={faStar} className="h-6 w-6" />
      ))}
    </div>
  );

  if (!mappedQuote || !mappedName) return null;

  return (
    <section
      className={cn(
        "bg-brand-greens-light/60 text-brand-secondary-dark mx-auto max-w-5xl rounded-2xl px-6 py-12 text-center md:py-16",
        className,
      )}
    >
      {Stars}

      <H3 className="font-display mx-auto max-w-4xl text-2xl leading-tight md:text-4xl">
        {mappedQuote}
      </H3>

      <P className="font-display mt-6 text-2xl md:text-3xl" noSpacing>
        {mappedName}
      </P>

      {mappedSubtitle && (
        <P
          className="text-brand-moss-greens-90 mt-2 text-base opacity-80 md:text-lg"
          noSpacing
        >
          {mappedSubtitle}
        </P>
      )}
    </section>
  );
}

export default ReviewHighlight;
